import { computed, ref, watch } from 'vue'

// Track sources of offline state (raw, immediate detection)
const navigatorIsOffline = ref<boolean>(
  typeof navigator !== 'undefined' ? !navigator.onLine : false
)
const axiosIsOffline = ref<boolean>(false)

// Ground truth: a real API round-trip recently succeeded (or returned an HTTP
// error, which still proves the API is reachable). This OVERRIDES the two hints
// above. `navigator.onLine` is reliable when it says "online" and updates live,
// but it can get stuck reporting `false` on Electron/macOS (sleep/wake, VPN, a
// wifi switch) even while the network works — and nothing makes Chromium
// re-poll it. So we treat a stuck `false` (or an axios error) as a *suspicion*
// that triggers a probe, and let an actual successful request be the verdict.
const apiReachable = ref<boolean>(false)

// Raw offline state (immediate, not debounced)
const rawIsOffline = ref<boolean>(typeof navigator !== 'undefined' ? !navigator.onLine : false)

// The stuck-`navigator.onLine` problem that makes us probe on any suspicion is
// specifically an Electron/macOS quirk. On mobile (Capacitor) and web,
// `navigator.onLine` is trustworthy, so probing during a genuine outage — when
// the OS already knows it is offline — is pure waste. `window.mainApi` is the
// Electron-renderer signal used elsewhere (see useAnalytics.ts).
const isElectronRenderer =
  typeof window !== 'undefined' && !!(window as unknown as { mainApi?: unknown }).mainApi

// When to run the recovery probe. On Electron, whenever we look offline at all,
// because the OS flag may be a stuck `false`. Everywhere else, only the
// API-down-but-OS-up case (the original cold-boot false positive) — a genuine
// OS-reported offline needs no probing there.
const shouldProbe = computed(() =>
  isElectronRenderer ? rawIsOffline.value : axiosIsOffline.value && !navigatorIsOffline.value
)

// Debounced offline state - waits 5 seconds before showing offline to avoid brief network blips
// Initialize to false - the debounce logic will handle setting it to true after 5 seconds if needed
const globalIsOffline = ref<boolean>(false)

// Track pending offline timeout
let offlineTimeout: number | null = null

// Track app startup time - ignore offline state for first 5 seconds after startup
// This prevents false positives when network is still initializing on boot
const appStartTime = typeof window !== 'undefined' ? Date.now() : 0
const STARTUP_GRACE_PERIOD_MS = 5000 // 5 seconds

// Check if we're still in the startup grace period
const isInStartupGracePeriod = () => {
  if (typeof window === 'undefined') {
    return false
  }
  return Date.now() - appStartTime < STARTUP_GRACE_PERIOD_MS
}

// Update raw state based on all sources
const updateRawState = () => {
  // Either hint (OS or axios) makes us *suspect* offline, but a confirmed
  // reachable API overrides both. This is what lets a stuck `navigator.onLine
  // === false` (or a stale axios flag) stop showing the banner the moment a
  // real request succeeds, instead of waiting for the OS to un-stick itself.
  const suspected = navigatorIsOffline.value || axiosIsOffline.value
  rawIsOffline.value = suspected && !apiReachable.value
}

// Watch every source and update raw state
watch([navigatorIsOffline, axiosIsOffline, apiReachable], updateRawState, { immediate: true })

// Debounce offline detection: wait 5 seconds before showing offline, but show online immediately
watch(
  rawIsOffline,
  (newValue) => {
    // Only run in browser context (window exists)
    if (typeof window === 'undefined') {
      return
    }

    // Clear any pending offline timeout
    if (offlineTimeout !== null) {
      clearTimeout(offlineTimeout)
      offlineTimeout = null
    }

    if (newValue) {
      // Going offline: wait 5 seconds, then check again before showing
      // BUT: if we're in startup grace period, wait until grace period ends + 5 seconds
      const calculateDelay = () => {
        if (isInStartupGracePeriod()) {
          // Still in grace period - wait until it ends, then wait 5 more seconds
          const gracePeriodRemaining = STARTUP_GRACE_PERIOD_MS - (Date.now() - appStartTime)
          return gracePeriodRemaining + 5000
        } else {
          // Past grace period - wait 5 seconds as normal
          return 5000
        }
      }

      offlineTimeout = window.setTimeout(() => {
        // Re-check after delay - if still offline and past grace period, show the banner
        if (rawIsOffline.value && !isInStartupGracePeriod()) {
          globalIsOffline.value = true
        }
        offlineTimeout = null
      }, calculateDelay())
    } else {
      // Coming back online: show immediately (no delay)
      globalIsOffline.value = false
    }
  },
  { immediate: true }
)

// Active API recovery probe.
//
// `axiosIsOffline` is only ever cleared by a fresh, non-cached *successful*
// API response (see the interceptor in useNocalApi). Nothing re-checks the API
// on its own — the navigator interval below only re-reads `navigator.onLine`.
// So a cold-boot request that fails before the network stack is ready (a
// common macOS/Electron scenario) strands `axiosIsOffline = true`, and the
// offline banner lingers until some incidental request happens to succeed.
//
// This probe re-checks the API on an interval while we look offline-to-the-API
// but online-to-the-OS, so the banner clears within seconds of the network
// genuinely coming back (often before the debounce even shows it).
let connectionProbe: (() => Promise<void>) | null = null
let recoveryTimeout: number | null = null
let recoveryActive = false
let recoveryAttempts = 0

// Fast first probe so a cold-boot false positive clears within seconds, then
// exponential backoff up to a 30s ceiling. The fast cadence only needs to
// survive the brief cold-boot window; during a *sustained* real outage (which
// looks identical to the OS — online but the API unreachable) backing off keeps
// this from hammering the API / draining mobile battery every few seconds.
const RECOVERY_PROBE_BASE_MS = 3000
const RECOVERY_PROBE_MAX_MS = 30000

const nextRecoveryDelay = () =>
  Math.min(RECOVERY_PROBE_BASE_MS * 2 ** recoveryAttempts, RECOVERY_PROBE_MAX_MS)

// Self-scheduling chain: the next probe is queued only after the current one
// settles, so probes never stack even on a slow/hanging network.
const scheduleRecoveryProbe = () => {
  if (recoveryTimeout !== null || typeof window === 'undefined') {
    return
  }
  recoveryTimeout = window.setTimeout(() => {
    recoveryTimeout = null
    // testConnection throws synchronously if no access token is set, and
    // rejects while still offline — wrap so a thrown/rejected probe never
    // surfaces as an unhandled error. A success reaches the axios interceptor,
    // which clears the offline state (and stops us via the watch below).
    Promise.resolve()
      .then(() => connectionProbe?.())
      .catch(() => {
        // Expected while still offline; the interceptor keeps state as-is.
      })
      .finally(() => {
        if (!recoveryActive) {
          return
        }
        recoveryAttempts += 1
        scheduleRecoveryProbe()
      })
  }, nextRecoveryDelay())
}

const startRecoveryProbe = () => {
  if (recoveryActive) {
    return
  }
  recoveryActive = true
  recoveryAttempts = 0
  scheduleRecoveryProbe()
}

const stopRecoveryProbe = () => {
  recoveryActive = false
  recoveryAttempts = 0
  if (recoveryTimeout !== null) {
    clearTimeout(recoveryTimeout)
    recoveryTimeout = null
  }
}

// Probe while `shouldProbe` holds. On Electron that is any suspicion of offline
// (the OS flag can be a stuck `false`, so a real round-trip is the only way to
// discover we are actually back); elsewhere it is only the API-down-but-OS-up
// case. A success clears the state via the axios interceptor →
// setAxiosOnlineState(false) → apiReachable, which flips the predicate false
// and stops us. A genuine outage just makes probes fail (with backoff).
watch(
  shouldProbe,
  (probe) => {
    if (probe) {
      startRecoveryProbe()
    } else {
      stopRecoveryProbe()
    }
  },
  { immediate: true }
)

/**
 * Register the API connectivity probe used to actively recover from a stranded
 * offline state (typically apiStore's testConnection). Called once at app
 * startup. If we're already stranded offline when the probe registers, start
 * recovering immediately rather than waiting for the next state transition; if
 * the loop is already running (with no-op probes), reset the backoff so the
 * first real probe fires promptly.
 */
export function registerConnectionProbe(probe: () => Promise<void>) {
  connectionProbe = probe
  if (!shouldProbe.value) {
    return
  }
  if (recoveryActive) {
    recoveryAttempts = 0
    if (recoveryTimeout !== null) {
      clearTimeout(recoveryTimeout)
      recoveryTimeout = null
    }
    scheduleRecoveryProbe()
  } else {
    startRecoveryProbe()
  }
}

// Dev-only: HMR re-evaluates this module and resets the timer handle, orphaning
// any in-flight recovery timeout. Clear it on dispose so reloads don't leak.
if (import.meta.hot) {
  import.meta.hot.dispose(() => stopRecoveryProbe())
}

// Apply an OS-level offline reading to navigator state. On a fresh
// online → offline *transition* it also invalidates any prior "reachable"
// verdict, so a genuine drop still surfaces even when the DOM `offline` event
// is missed (unreliable on Electron/macOS — the reason the 10s poll exists).
// It must clear `apiReachable` only on the transition, NOT on a sustained
// offline reading: a stuck `navigator.onLine === false` would otherwise wipe a
// probe-confirmed recovery every poll and re-raise the banner. Shared by the
// online/offline events and the poll so all three stay consistent.
const applyNavigatorState = (nowOffline: boolean) => {
  if (nowOffline && !navigatorIsOffline.value) {
    apiReachable.value = false
  }
  navigatorIsOffline.value = nowOffline
}

const checkConnection = () => {
  if (typeof navigator !== 'undefined') {
    applyNavigatorState(!navigator.onLine)
  }
}

// Track if listeners are already set up (to avoid duplicates)
let listenersInitialized = false

// Set up navigator listeners and body class watcher once globally
const initializeListeners = () => {
  if (listenersInitialized || typeof window === 'undefined') {
    return
  }

  // Set up navigator event listeners
  window.addEventListener('online', () => applyNavigatorState(false))
  window.addEventListener('offline', () => applyNavigatorState(true))
  window.setInterval(checkConnection, 10000)

  // Set up body class watcher once globally
  const updateBodyClass = () => {
    if (typeof document !== 'undefined') {
      if (globalIsOffline.value) {
        document.body.classList.add('is-offline')
      } else {
        document.body.classList.remove('is-offline')
      }
    }
  }
  watch(globalIsOffline, updateBodyClass, { immediate: true })

  listenersInitialized = true

  // Initial check
  checkConnection()
}

/**
 * Set the offline state from axios interceptors
 * This allows API-specific failure detection to inform the global offline state
 */
export function setAxiosOnlineState(isOffline: boolean) {
  axiosIsOffline.value = isOffline
  // The axios interceptor calls this from a *real* round-trip: success and HTTP
  // errors both mean the API is reachable (isOffline === false); only a network
  // error (no response) means unreachable (isOffline === true). Record that as
  // the authoritative reachability verdict so it can override a stuck
  // navigator.onLine.
  apiReachable.value = !isOffline
}

/**
 * Composable to track online/offline state
 * Uses navigator.onLine for immediate feedback and listens to online/offline events
 * Can also be informed by axios interceptors via setAxiosOnlineState()
 *
 * Returns the global offline state that combines:
 * - Browser navigator.onLine (real-time network status)
 * - Axios interceptor state (API-specific connectivity)
 *
 * Note: Listeners and body class watcher are set up once globally on first use,
 * so this composable can be called from multiple places without creating duplicates.
 * No lifecycle hooks needed - just returns the reactive state.
 */
export function useOffline() {
  // Initialize listeners on first use (only happens once)
  initializeListeners()

  return {
    isOffline: globalIsOffline,
    checkConnection
  }
}
